import { ViewTimeInference } from "@/dataprocessor/view-time";
import { CartObserverProvider } from "../../cart";
import type { SetupAttributeType } from "../types";
import type {
  BehaviorSecondaryAttributes,
  BehaviorTertiaryAttributes,
} from "./types";

export function prepare() {
  if (!window.helium) {
    window.helium = {} as any;
  }

  window.helium.attributes = {
    ...(window?.helium?.attributes || {}),
    behavior: {
      ...(window?.helium?.attributes?.behavior || {}),
      secondary: {
        ...(window?.helium?.attributes?.behavior?.secondary || {}),
        temp: {
          ...(window?.helium?.attributes?.behavior?.secondary?.temp || {}),
        },
      },
      tertiary: {
        ...(window?.helium?.attributes?.behavior?.tertiary || {}),
        temp: {
          ...(window?.helium?.attributes?.behavior?.tertiary?.temp || {}),
        },
      },
    },
  };
}

export const behaviorSecondaryAttributes: SetupAttributeType<BehaviorSecondaryAttributes> =
  [
    {
      id: "scrollSpeed",
      setup: () => {
        const SCROLL_SPEED_THRESHOLD = 5;
        let combinedScrollSpeed = 0;

        // Load previous scroll speed if it exists
        if (window.helium.attributes.behavior.secondary.scrollSpeed) {
          combinedScrollSpeed =
            window.helium.attributes.behavior.secondary.scrollSpeed;
        }

        if (!window.helium.attributes.behavior.secondary.temp.scrollPositions) {
          window.helium.attributes.behavior.secondary.temp = {
            ...window.helium.attributes.behavior.secondary.temp,
            scrollPositions: [],
          };
        }

        window.addEventListener("scroll", () => {
          const currentScrollTop =
            window.scrollY || document.documentElement.scrollTop;
          const currentTime = Date.now();

          window.helium.attributes.behavior.secondary.temp.scrollPositions.push(
            {
              position: currentScrollTop,
              time: currentTime,
            },
          );

          if (
            window.helium.attributes.behavior.secondary.temp.scrollPositions
              .length > SCROLL_SPEED_THRESHOLD
          ) {
            window.helium.attributes.behavior.secondary.temp.scrollPositions.shift();
          }

          if (
            window.helium.attributes.behavior.secondary.temp.scrollPositions
              .length >= SCROLL_SPEED_THRESHOLD
          ) {
            const scrollData =
              window.helium.attributes.behavior.secondary.temp.scrollPositions;
            const totalDeltaY =
              scrollData[scrollData.length - 1].position -
              scrollData[0].position;
            const totalDeltaTime =
              scrollData[scrollData.length - 1].time - scrollData[0].time || 1;

            const avgSpeed = Math.abs(totalDeltaY) / totalDeltaTime;
            const normalizedSpeed = Math.min(100, avgSpeed * 100);

            combinedScrollSpeed =
              (combinedScrollSpeed * (SCROLL_SPEED_THRESHOLD - 1) +
                normalizedSpeed) /
              SCROLL_SPEED_THRESHOLD;

            const scrollSpeedCategory =
              combinedScrollSpeed > 30
                ? "High"
                : combinedScrollSpeed > 15
                  ? "Medium"
                  : "Low";
            window.helium.attributes.behavior.secondary.scrollSpeed =
              scrollSpeedCategory;
          }
        });

        return window.helium.attributes.behavior.secondary.scrollSpeed;
      },
    },
    {
      id: "accessTime",
      setup: () => {
        if (!window.helium.attributes.behavior.secondary.accessTime) {
          const hours = new Date().getHours();
          let accessTimeCategory = "Night";

          if (hours >= 6 && hours < 12) accessTimeCategory = "Morning";
          else if (hours >= 12 && hours < 18) accessTimeCategory = "Afternoon";
          else if (hours >= 18 && hours < 24) accessTimeCategory = "Evening";

          window.helium.attributes.behavior.secondary.accessTime =
            accessTimeCategory;
        }

        return window.helium.attributes.behavior.secondary.accessTime;
      },
    },
    {
      id: "scrollDepthPercentage",
      setup: () => {
        window.addEventListener("scroll", () => {
          const scrollHeight =
            document.documentElement.scrollHeight - window.innerHeight;
          const scrollTop =
            window.scrollY || document.documentElement.scrollTop;
          const scrollPercentage = (scrollTop / scrollHeight) * 100;

          const scrollDepthCategory =
            scrollPercentage > 66
              ? "Deep"
              : scrollPercentage > 33
                ? "Moderate"
                : "Shallow";
          const precedence = ["Shallow", "Moderate", "Deep"];

          const higherPrecedenceExists =
            window.helium.attributes.behavior.secondary.scrollDepthPercentage &&
            precedence.indexOf(
              window.helium.attributes.behavior.secondary.scrollDepthPercentage,
            ) > precedence.indexOf(scrollDepthCategory);

          if (!higherPrecedenceExists) {
            window.helium.attributes.behavior.secondary.scrollDepthPercentage =
              scrollDepthCategory;
          }

          return window.helium.attributes.behavior.secondary
            .scrollDepthPercentage;
        });
      },
    },
    {
      id: "timeOnPage",
      setup: () => {
        if (!window.helium.attributes.behavior.secondary.temp.pageStartTime) {
          window.helium.attributes.behavior.secondary.temp.pageStartTime =
            Date.now();
        }

        setInterval(() => {
          const currentTime = Date.now();
          const timeOnPage =
            (currentTime -
              window.helium.attributes.behavior.secondary.temp.pageStartTime) /
            1000;

          let timeOnPageCategory = "Short";

          if (timeOnPage <= 5) timeOnPageCategory = "Bounce";
          else if (timeOnPage <= 30) timeOnPageCategory = "Short";
          else if (timeOnPage <= 90) timeOnPageCategory = "Medium";
          else if (timeOnPage <= 180) timeOnPageCategory = "Long";
          else timeOnPageCategory = "Very Long";

          window.helium.attributes.behavior.secondary.timeOnPage =
            timeOnPageCategory;

          return window.helium.attributes.behavior.secondary.timeOnPage;
        }, 1000);
      },
    },
    {
      id: "clicksPerPage",
      setup: () => {
        if (!window.helium.attributes.behavior.secondary.temp.clickCount) {
          window.helium.attributes.behavior.secondary.temp.clickCount = 0;
        }

        document.addEventListener("click", () => {
          window.helium.attributes.behavior.secondary.temp.clickCount++;

          const clickIntensity =
            window.helium.attributes.behavior.secondary.temp.clickCount > 3
              ? "High"
              : window.helium.attributes.behavior.secondary.temp.clickCount >= 2
                ? "Moderate"
                : "Low";

          window.helium.attributes.behavior.secondary.clicksPerPage =
            clickIntensity;

          return window.helium.attributes.behavior.secondary.clicksPerPage;
        });
      },
    },
    {
      id: "tabVisibilityChanges",
      setup: () => {
        if (
          !window.helium.attributes.behavior.secondary.temp
            .visibilityChangeCount
        ) {
          window.helium.attributes.behavior.secondary.temp.visibilityChangeCount = 0;
        }

        document.addEventListener("visibilitychange", () => {
          if (document.hidden) {
            window.helium.attributes.behavior.secondary.temp
              .visibilityChangeCount++;
          }

          const visibilityChangeCategory =
            window.helium.attributes.behavior.secondary.temp
              .visibilityChangeCount > 4
              ? "Very Frequent"
              : window.helium.attributes.behavior.secondary.temp
                    .visibilityChangeCount >= 2
                ? "Moderate"
                : "Rare";

          window.helium.attributes.behavior.secondary.tabVisibilityChanges =
            visibilityChangeCategory;

          return window.helium.attributes.behavior.secondary
            .tabVisibilityChanges;
        });
      },
    },
    {
      id: "deviceOrientationChange",
      setup: () => {
        if (
          !window.helium.attributes.behavior.secondary.temp
            .orientationChangeCount
        ) {
          window.helium.attributes.behavior.secondary.temp.orientationChangeCount = 0;
        }

        window.addEventListener("orientationchange", () => {
          window.helium.attributes.behavior.secondary.temp
            .orientationChangeCount++;

          const orientationCategory =
            window.helium.attributes.behavior.secondary.temp
              .orientationChangeCount > 3
              ? "Frequent"
              : window.helium.attributes.behavior.secondary.temp
                    .orientationChangeCount > 1
                ? "Occasional"
                : "Rare";

          window.helium.attributes.behavior.secondary.deviceOrientationChange =
            orientationCategory;

          return window.helium.attributes.behavior.secondary
            .deviceOrientationChange;
        });
      },
    },
    {
      id: "navigationPathDepth",
      setup: () => {
        if (!window.helium.attributes.behavior.secondary.temp.pageDepth) {
          window.helium.attributes.behavior.secondary.temp.pageDepth = 0;
        }

        window.addEventListener("popstate", () => {
          window.helium.attributes.behavior.secondary.temp.pageDepth++;

          const pathDepthCategory =
            window.helium.attributes.behavior.secondary.temp.pageDepth > 15
              ? "Deep"
              : window.helium.attributes.behavior.secondary.temp.pageDepth > 7
                ? "Moderate"
                : "Shallow";

          window.helium.attributes.behavior.secondary.navigationPathDepth =
            pathDepthCategory;

          return window.helium.attributes.behavior.secondary
            .navigationPathDepth;
        });
      },
    },
    {
      id: "resourceLoadTime",
      setup: () => {
        if (!window.helium.attributes.behavior.secondary.resourceLoadTime) {
          const resourceLoadTime =
            performance.timing.loadEventEnd -
            performance.timing.navigationStart;
          const loadTimeCategory =
            resourceLoadTime > 7000
              ? "Slow"
              : resourceLoadTime > 3000
                ? "Moderate"
                : "Fast";

          window.helium.attributes.behavior.secondary.resourceLoadTime =
            loadTimeCategory;
        }

        return window.helium.attributes.behavior.secondary.resourceLoadTime;
      },
    },
    {
      id: "sessionDuration",
      setup: () => {
        if (!window.helium.attributes.behavior.secondary.temp.sessionStart) {
          window.helium.attributes.behavior.secondary.temp.sessionStart =
            Date.now();
        }

        window.addEventListener("beforeunload", () => {
          const sessionEnd = Date.now();
          const sessionDuration =
            (sessionEnd -
              window.helium.attributes.behavior.secondary.temp.sessionStart) /
            1000;

          const sessionCategory =
            sessionDuration > 1800
              ? "Very Long"
              : sessionDuration > 900
                ? "Long"
                : sessionDuration > 300
                  ? "Medium"
                  : "Short";

          window.helium.attributes.behavior.secondary.sessionDuration =
            sessionCategory;

          return window.helium.attributes.behavior.secondary.sessionDuration;
        });
      },
    },
  ];

export const behaviorTertiaryAttributes: SetupAttributeType<BehaviorTertiaryAttributes> =
  [
    {
      id: "cartInteractionDepth",
      setup: () => {
        const addCount = CartObserverProvider.getCartOperationCount("add");
        const updateCount =
          CartObserverProvider.getCartOperationCount("update");
        const removeCount =
          CartObserverProvider.getCartOperationCount("remove");

        const cartInteractionDepth =
          (addCount + updateCount + removeCount) * 10;

        const depthCategory =
          cartInteractionDepth > 66
            ? "High"
            : cartInteractionDepth > 33
              ? "Moderate"
              : "Low";

        window.helium.attributes.behavior.tertiary.cartInteractionDepth =
          depthCategory;

        return window.helium.attributes.behavior.tertiary.cartInteractionDepth;
      },
    },
    {
      id: "addToCartVelocity",
      setup: () => {
        const addOperations =
          CartObserverProvider.getAllCartOperations().filter(
            (op) => op.action === "add",
          );

        if (addOperations.length === 0) {
          window.helium.attributes.behavior.tertiary.addToCartVelocity =
            "Moderate";
          return window.helium.attributes.behavior.tertiary.addToCartVelocity;
        }

        const addTimes = addOperations.map(
          (op) =>
            new Date(op.timestamp).getTime() -
            new Date(addOperations[0].timestamp).getTime(),
        );

        const avgAddToCartTime =
          addTimes.reduce((acc, time) => acc + time, 0) / (addTimes.length - 1);

        const velocityCategory =
          avgAddToCartTime < 5000
            ? "Fast"
            : avgAddToCartTime < 10000
              ? "Moderate"
              : "Slow";

        window.helium.attributes.behavior.tertiary.addToCartVelocity =
          velocityCategory;

        return window.helium.attributes.behavior.tertiary.addToCartVelocity;
      },
    },
    {
      id: "frequentCartModifications",
      setup: () => {
        const checkoutRevisits =
          CartObserverProvider.getCartOperationCount("update") +
          CartObserverProvider.getCartOperationCount("remove");

        const modificationCategory =
          checkoutRevisits > 10
            ? "High"
            : checkoutRevisits > 5
              ? "Moderate"
              : "Low";

        window.helium.attributes.behavior.tertiary.frequentCartModifications =
          modificationCategory;

        return window.helium.attributes.behavior.tertiary
          .frequentCartModifications;
      },
    },
    {
      id: "productIntentConcentration",
      setup: () => {
        const totalSectionsIntent = Object.values(
          ViewTimeInference.sectionScore,
        ).reduce((acc, score) => acc + score, 0);
        const totalSections = Object.keys(
          ViewTimeInference.sectionScore,
        ).length;

        const sectionIntentConcentration =
          (totalSectionsIntent / totalSections) * 100;

        const concentrationCategory =
          sectionIntentConcentration > 66
            ? "High"
            : sectionIntentConcentration > 33
              ? "Moderate"
              : "Low";

        window.helium.attributes.behavior.tertiary.productIntentConcentration =
          concentrationCategory;

        return window.helium.attributes.behavior.tertiary
          .productIntentConcentration;
      },
    },
    {
      id: "startToFirstAddToCartLag",
      setup: () => {
        const addOperations =
          CartObserverProvider.getAllCartOperations().filter(
            (op) => op.action === "add",
          );

        if (addOperations.length === 0) {
          window.helium.attributes.behavior.tertiary.startToFirstAddToCartLag =
            "High";
          return window.helium.attributes.behavior.tertiary
            .startToFirstAddToCartLag;
        }

        const firstInteractionTime =
          (window.helium.logs[0].time as number) || Date.now();
        const firstAddToCartTime = new Date(
          addOperations[0].timestamp,
        ).getTime();

        const lag = (firstAddToCartTime - firstInteractionTime) / 1000;

        const lagCategory =
          lag < 300 ? "Low" : lag < 1200 ? "Moderate" : "High";

        window.helium.attributes.behavior.tertiary.startToFirstAddToCartLag =
          lagCategory;

        return window.helium.attributes.behavior.tertiary
          .startToFirstAddToCartLag;
      },
    },
    {
      id: "hoverToClickRatio",
      setup: () => {
        const hoverCount =
          window.helium.attributes.behavior.secondary.hoverDuration || 0;
        const clickCount =
          window.helium.attributes.behavior.secondary.temp.clickCount || 1;

        const hoverToClickRatio = (+hoverCount / +clickCount) * 100;

        const ratioCategory =
          hoverToClickRatio > 66
            ? "Hover-Dominant"
            : hoverToClickRatio > 33
              ? "Balanced"
              : "Click-Dominant";

        window.helium.attributes.behavior.tertiary.hoverToClickRatio =
          ratioCategory;

        return window.helium.attributes.behavior.tertiary.hoverToClickRatio;
      },
    },
    {
      id: "scrollStopFrequency",
      setup: () => {
        const scrollStops =
          window.helium.attributes.behavior.secondary.scrollDepthPercentage ||
          0;
        const sessionDuration =
          window.helium.attributes.behavior.secondary.sessionDuration || 1;

        const scrollStopFrequency = (scrollStops / sessionDuration) * 100;

        const stopFrequencyCategory =
          scrollStopFrequency > 66
            ? "High"
            : scrollStopFrequency > 33
              ? "Moderate"
              : "Low";

        window.helium.attributes.behavior.tertiary.scrollStopFrequency =
          stopFrequencyCategory;

        return window.helium.attributes.behavior.tertiary.scrollStopFrequency;
      },
    },
  ];
